import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { siteConfig } from '../data/config';
import SEO, { pageSEO } from '../components/SEO';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { 
  Phone, Calendar, Car, Users, Accessibility, Building2, 
  Plane, Zap, Clock, Shield, CreditCard, MapPin, 
  ChevronRight, Apple, Play, CheckCircle2
} from 'lucide-react';

const HomePage = () => {
  const { t, getRoute } = useLanguage();

  const services = [
    { 
      icon: Car, 
      title: t('services.sedan.title'), 
      desc: t('services.sedan.desc'),
      color: 'bg-blue-50 text-primary',
    },
    { 
      icon: Users, 
      title: t('services.minivan.title'), 
      desc: t('services.minivan.desc'),
      color: 'bg-amber-50 text-amber-600',
    },
    { 
      icon: Accessibility, 
      title: t('services.accessible.title'), 
      desc: t('services.accessible.desc'),
      color: 'bg-green-50 text-green-600',
    },
    { 
      icon: Building2, 
      title: t('services.corporate.title'), 
      desc: t('services.corporate.desc'),
      color: 'bg-purple-50 text-purple-600',
    },
    { 
      icon: Plane, 
      title: t('services.airport.title'), 
      desc: t('services.airport.desc'),
      color: 'bg-sky-50 text-sky-600',
    },
    { 
      icon: Zap, 
      title: t('services.express.title'), 
      desc: t('services.express.desc'),
      color: 'bg-orange-50 text-orange-600',
    },
  ];

  const trustBadges = [
    { icon: Clock, label: '24/7' },
    { icon: Shield, label: 'Safe & Reliable' },
    { icon: CreditCard, label: 'All Cards Accepted' },
    { icon: MapPin, label: 'Greater Montreal' },
  ];

  return (
    <div className="min-h-screen" data-testid="home-page">
      <SEO {...pageSEO.home} />
      
      {/* Hero Section */}
      <section 
        className="relative min-h-[90vh] flex items-center justify-center overflow-hidden bg-primary"
      >
        <div className="absolute inset-0 bg-gradient-to-b from-primary to-primary/90" />
        
        <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-center py-16 md:py-24">
          {/* Main Logo Image */}
          <img 
            src="/images/mtl-taxi-logo-electric-suv-montreal.png"
            alt="MTL.TAXI electric SUV taxi service in Montreal"
            title="MTL.TAXI Montreal electric taxi service"
            width="600"
            height="200"
            loading="eager"
            className="mx-auto mb-8 w-full max-w-xl md:max-w-2xl h-auto"
          />
          
          <h1 
            className="font-heading font-black text-3xl sm:text-4xl md:text-5xl lg:text-6xl text-white mb-6 tracking-tight leading-tight"
            data-testid="hero-title"
          >
            {t('hero.title')}
          </h1>
          <p 
            className="text-lg md:text-xl text-white/90 mb-10 max-w-2xl mx-auto"
            data-testid="hero-subtitle"
          >
            {t('hero.subtitle')}
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
            <Button
              asChild
              size="lg"
              className="bg-accent text-accent-foreground hover:bg-accent/90 font-bold text-lg h-14 px-8 shadow-xl hover:shadow-2xl transition-all hover:-translate-y-0.5"
              data-testid="hero-call-btn"
            >
              <a href={siteConfig.phoneTel}>
                <Phone className="w-5 h-5 mr-2" />
                {t('hero.callNow')} - {siteConfig.phone}
              </a>
            </Button>
            <Button
              asChild
              size="lg"
              variant="outline"
              className="bg-white/10 backdrop-blur-sm border-white/30 text-white hover:bg-white/20 font-bold text-lg h-14 px-8"
              data-testid="hero-book-btn"
            >
              <a href={siteConfig.bookingUrl} target="_blank" rel="noopener noreferrer">
                <Calendar className="w-5 h-5 mr-2" />
                {t('hero.bookOnline')}
              </a>
            </Button>
          </div>

          {/* Trust Badges */}
          <div className="flex flex-wrap justify-center gap-6 md:gap-8">
            {trustBadges.map((badge, index) => {
              const Icon = badge.icon;
              return (
                <div key={index} className="flex items-center gap-2 text-white/80">
                  <Icon className="w-5 h-5" />
                  <span className="text-sm font-medium">{badge.label}</span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 rounded-full border-2 border-white/50 flex items-start justify-center p-2">
            <div className="w-1 h-2 bg-white/70 rounded-full" />
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 md:py-28 bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-heading font-bold text-3xl md:text-4xl text-foreground mb-4">
              {t('services.title')}
            </h2>
            <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
              {t('services.subtitle')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service, index) => {
              const Icon = service.icon;
              return (
                <Card 
                  key={index}
                  className="group hover:shadow-lg transition-all duration-300 hover:-translate-y-1 border-0 shadow-md"
                  data-testid={`service-card-${index}`}
                >
                  <CardContent className="p-6">
                    <div className={`w-14 h-14 rounded-xl ${service.color} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                      <Icon className="w-7 h-7" />
                    </div>
                    <h3 className="font-heading font-bold text-xl text-foreground mb-2">
                      {service.title}
                    </h3>
                    <p className="text-muted-foreground">
                      {service.desc}
                    </p>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          <div className="text-center mt-12">
            <Button asChild variant="outline" size="lg" className="font-semibold">
              <Link to={getRoute('/services')}>
                {t('services.title')}
                <ChevronRight className="w-4 h-4 ml-1" />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Airport CTA Section */}
      <section className="py-20 md:py-28 bg-primary text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-white/20 px-4 py-2 rounded-full text-sm font-medium mb-6">
                <Plane className="w-4 h-4" />
                Aéroport Montréal-Trudeau (YUL)
              </div>
              <h2 className="font-heading font-bold text-3xl md:text-4xl mb-6">
                {t('airport.title')}
              </h2>
              <p className="text-white/90 text-lg mb-8">
                {t('airport.intro')}
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button
                  asChild
                  size="lg"
                  className="bg-accent text-accent-foreground hover:bg-accent/90 font-bold"
                >
                  <Link to={getRoute('/airport')}>
                    {t('airport.rates.title')}
                    <ChevronRight className="w-4 h-4 ml-1" />
                  </Link>
                </Button>
                <Button
                  asChild
                  size="lg"
                  variant="outline"
                  className="border-white/30 text-white hover:bg-white/10"
                >
                  <a href={siteConfig.bookingUrl} target="_blank" rel="noopener noreferrer">
                    {t('hero.bookOnline')}
                  </a>
                </Button>
              </div>
            </div>
            <div className="relative">
              <img
                src="/images/montreal-airport-taxi-transfer-arrivals.png"
                alt="MTL Taxi airport transfer service - Professional taxi at Montreal-Trudeau YUL arrivals terminal"
                title="Montreal airport taxi transfer - MTL Taxi YUL service"
                width="800"
                height="600"
                loading="lazy"
                className="rounded-2xl shadow-2xl w-full h-auto"
              />
              <div className="absolute -bottom-6 -left-6 bg-white text-foreground p-4 rounded-xl shadow-lg hidden md:block">
                <div className="text-sm text-muted-foreground">Centre-ville → YUL</div>
                <div className="font-heading font-bold text-2xl text-primary">~48$</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* App Download Section */}
      <section className="py-20 md:py-28 bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-3xl p-8 md:p-12 lg:p-16">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div className="text-white">
                <h2 className="font-heading font-bold text-3xl md:text-4xl mb-6">
                  {t('contact.appTitle')}
                </h2>
                <p className="text-gray-300 text-lg mb-8">
                  {t('contact.appDesc')}
                </p>
                <ul className="space-y-3 mb-8">
                  {[
                    'Commandez en quelques clics',
                    'Suivez votre taxi en temps réel',
                    'Estimez le prix de votre course',
                    'Payez directement dans l\'app',
                  ].map((item, index) => (
                    <li key={index} className="flex items-center gap-3 text-gray-300">
                      <CheckCircle2 className="w-5 h-5 text-accent flex-shrink-0" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
                <div className="flex flex-col sm:flex-row gap-4">
                  <a
                    href={siteConfig.appStoreUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-3 bg-white text-foreground px-6 py-3 rounded-xl hover:bg-gray-100 transition-colors"
                    data-testid="app-store-link"
                  >
                    <Apple className="w-8 h-8" />
                    <div className="text-left">
                      <div className="text-xs text-muted-foreground">Download on the</div>
                      <div className="font-semibold">App Store</div>
                    </div>
                  </a>
                  <a
                    href={siteConfig.playStoreUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-3 bg-white text-foreground px-6 py-3 rounded-xl hover:bg-gray-100 transition-colors"
                    data-testid="play-store-link"
                  >
                    <Play className="w-8 h-8" />
                    <div className="text-left">
                      <div className="text-xs text-muted-foreground">Get it on</div>
                      <div className="font-semibold">Google Play</div>
                    </div>
                  </a>
                </div>
              </div>
              <div className="hidden md:flex justify-center">
                <div className="relative">
                  <div className="w-64 h-[500px] bg-gray-700 rounded-[3rem] p-3 shadow-2xl">
                    <div className="w-full h-full bg-primary rounded-[2.5rem] flex items-center justify-center">
                      <div className="text-center text-white">
                        <Car className="w-16 h-16 mx-auto mb-4" />
                        <div className="font-heading font-bold text-2xl">MTL Taxi</div>
                        <div className="text-sm text-white/70 mt-2">App Preview</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Quick FAQ Section */}
      <section className="py-20 md:py-28 bg-secondary/30">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading font-bold text-3xl md:text-4xl text-foreground mb-12">
            {t('faq.title')}
          </h2>
          <div className="grid gap-6 text-left">
            {t('faq.questions').slice(0, 4).map((faq, index) => (
              <Card key={index} className="border-0 shadow-md">
                <CardContent className="p-6">
                  <h3 className="font-heading font-bold text-lg text-foreground mb-2">
                    {faq.q}
                  </h3>
                  <p className="text-muted-foreground">{faq.a}</p>
                </CardContent>
              </Card>
            ))}
          </div>
          <Button asChild variant="outline" size="lg" className="mt-8 font-semibold">
            <Link to={getRoute('/faq')}>
              {t('faq.title')}
              <ChevronRight className="w-4 h-4 ml-1" />
            </Link>
          </Button>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 md:py-20 bg-accent">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading font-bold text-2xl md:text-3xl text-accent-foreground mb-6">
            {t('hero.title')}
          </h2>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              asChild
              size="lg"
              className="bg-foreground text-white hover:bg-foreground/90 font-bold text-lg h-14 px-8"
            >
              <a href={siteConfig.phoneTel}>
                <Phone className="w-5 h-5 mr-2" />
                {siteConfig.phone}
              </a>
            </Button>
            <Button
              asChild
              size="lg"
              variant="outline"
              className="border-foreground/30 text-foreground hover:bg-foreground/5 font-bold text-lg h-14 px-8"
            >
              <a href={siteConfig.bookingUrl} target="_blank" rel="noopener noreferrer">
                <Calendar className="w-5 h-5 mr-2" />
                {t('hero.bookOnline')}
              </a>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
